import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n/I18nContext";
import { api, errMsg } from "../lib/api";
import { Minus, Plus, X, Truck } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "../components/ImagePicker";

export default function Cart() {
  const { items, setQty, remove, subtotal } = useCart();
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [shippingMethods, setShippingMethods] = useState([]);

  useEffect(() => {
    api.get("/shipping-methods").then((r) => setShippingMethods(r.data)).catch(() => {});
  }, []);

  const cheapest = shippingMethods.length
    ? Math.min(...shippingMethods.map((m) => m.price))
    : null;

  const [checkoutUrl, setCheckoutUrl] = useState(null);

  const checkout = async () => {
    setLoading(true);
    setCheckoutUrl(null);
    try {
      const res = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, note: i.note || "" })),
        origin_url: window.location.origin,
        language: lang,
      });
      const url = res.data.checkout_url;
      setCheckoutUrl(url);
      // Try automatic redirect (may fail silently on some iOS Safari builds)
      try { window.location.assign(url); } catch { /* fallback shown below */ }
      // Belt-and-suspenders: if we are still here after 1.5 s, force it
      setTimeout(() => {
        if (typeof window !== "undefined" && document.visibilityState === "visible") {
          window.location.href = url;
        }
      }, 1500);
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(false);
    }
  };

  return (
    <main className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="cart-page">
      <div className="label-caps text-muted-foreground mb-4">{t("nav.cart")}</div>
      <h1 className="font-hand text-6xl md:text-7xl mb-14">{t("cart.title")}</h1>

      {items.length === 0 ? (
        <div className="border-t border-border pt-16">
          <p className="text-muted-foreground mb-8" data-testid="cart-empty">{t("cart.empty")}</p>
          <Link to="/shop" className="btn-outline">{t("cart.browse")}</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2 border-t border-border">
            {items.map((i) => (
              <div key={i.key} className="flex gap-6 py-8 border-b border-border" data-testid={`cart-item-${i.product_id}`}>
                <div className="product-image-wrap w-24 h-32 md:w-32 md:h-40 shrink-0">
                  <img src={resolveImageUrl(i.image_url)} alt={i.name[lang]} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-hand text-3xl mb-1">{i.name[lang]}</h3>
                      <div className="text-sm text-muted-foreground">{i.price.toFixed(2).replace(".", ",")} € / {t("cart.each")}</div>
                      {i.note && (
                        <div className="text-sm italic text-foreground/70 mt-2 whitespace-pre-wrap" data-testid={`cart-note-${i.product_id}`}>
                          {t("product.noteLabel")}: “{i.note}”
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => remove(i.key)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      data-testid={`remove-${i.product_id}`}
                      aria-label={t("cart.remove")}
                    >
                      <X strokeWidth={1.25} className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-border">
                      <button
                        onClick={() => setQty(i.key, i.quantity - 1)}
                        className="w-9 h-9 flex items-center justify-center hover:bg-secondary transition-colors"
                        data-testid={`qty-dec-${i.product_id}`}
                      >
                        <Minus strokeWidth={1.25} className="w-4 h-4" />
                      </button>
                      <div className="w-10 text-center text-sm">{i.quantity}</div>
                      <button
                        onClick={() => setQty(i.key, i.quantity + 1)}
                        className="w-9 h-9 flex items-center justify-center hover:bg-secondary transition-colors"
                        data-testid={`qty-inc-${i.product_id}`}
                      >
                        <Plus strokeWidth={1.25} className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="font-hand text-xl">{(i.price * i.quantity).toFixed(2).replace(".", ",")} €</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-1">
            <div className="border border-border p-8 bg-[#EAE6D9]/40 sticky top-24">
              <div className="label-caps text-muted-foreground mb-6">{t("cart.subtotal")}</div>
              <div className="font-hand text-5xl mb-6 text-[hsl(var(--primary))]" data-testid="cart-subtotal">
                {subtotal.toFixed(2).replace(".", ",")} €
              </div>

              {shippingMethods.length > 0 && (
                <div className="border-t border-border pt-5 mb-6" data-testid="shipping-preview">
                  <div className="label-caps text-muted-foreground mb-3 flex items-center gap-2">
                    <Truck strokeWidth={1.5} className="w-3.5 h-3.5" />
                    Toimitusvaihtoehdot
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {shippingMethods.map((m) => (
                      <li key={m.id} className="flex items-baseline justify-between gap-3" data-testid={`shipping-opt-${m.id}`}>
                        <span className="text-foreground/80">
                          {m.name[lang] || m.name.en}
                          <span className="text-muted-foreground text-xs ml-1.5">
                            ({m.delivery_min_days}–{m.delivery_max_days} pv)
                          </span>
                        </span>
                        <span className="tabular-nums font-medium">
                          {m.price === 0 ? "0,00 €" : `${m.price.toFixed(2).replace(".", ",")} €`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                    {cheapest === 0
                      ? "Valitse toimitustapa seuraavassa vaiheessa. Nouto studiolta on ilmainen."
                      : "Valitse toimitustapa seuraavassa vaiheessa."}
                  </p>
                </div>
              )}

              <button
                onClick={checkout}
                disabled={loading}
                className="btn-primary w-full justify-center"
                data-testid="checkout-btn"
              >
                {loading ? "Ohjataan Stripelle…" : t("cart.checkout")}
              </button>
              {checkoutUrl && (
                <a
                  href={checkoutUrl}
                  className="block mt-3 text-center text-sm text-[hsl(var(--primary))] underline underline-offset-4"
                  data-testid="checkout-fallback-link"
                >
                  Jos maksusivu ei aukene, klikkaa tästä →
                </a>
              )}
              <div className="mt-5 flex items-center justify-center gap-2 text-[10px] text-muted-foreground tracking-[0.15em] uppercase" data-testid="payment-methods">
                <span>Kortti</span>
                <span className="opacity-40">•</span>
                <span className="font-semibold text-[hsl(var(--primary))]">MobilePay</span>
                <span className="opacity-40">•</span>
                <span>Stripe</span>
              </div>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
